import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentToolbar = ({
  document,
  saving,
  lastSaved,
  onSave,
  onDownload,
  onToggleCollaboration,
  onToggleSidebar,
  showCollaboration,
  showSidebar,
  documentType,
  onExport,
  onShare,
  autoSaveStatus,
  isCollaborating,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSharePointUpload,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const exportOptions = [
    { id: 'pdf', label: 'Export as PDF', icon: 'FileText' },
    { id: 'docx', label: 'Export as Word', icon: 'FileText' },
    { id: 'xlsx', label: 'Export as Excel', icon: 'Table' },
    { id: 'txt', label: 'Export as Text', icon: 'FileText' }
  ];

  const shareOptions = [
    { id: 'link', label: 'Share Link', icon: 'Link' },
    { id: 'email', label: 'Send via Email', icon: 'Mail' },
    { id: 'collaborate', label: 'Invite Collaborators', icon: 'Users' }
  ];

  const formatButtons = [
    { id: 'bold', icon: 'Bold', tooltip: 'Bold (Ctrl+B)' },
    { id: 'italic', icon: 'Italic', tooltip: 'Italic (Ctrl+I)' },
    { id: 'underline', icon: 'Underline', tooltip: 'Underline (Ctrl+U)' },
    { id: 'strikethrough', icon: 'Strikethrough', tooltip: 'Strikethrough' }
  ];

  const alignmentButtons = [
    { id: 'align-left', icon: 'AlignLeft', tooltip: 'Align Left' },
    { id: 'align-center', icon: 'AlignCenter', tooltip: 'Align Center' },
    { id: 'align-right', icon: 'AlignRight', tooltip: 'Align Right' },
    { id: 'align-justify', icon: 'AlignJustify', tooltip: 'Justify' }
  ];

  const handleExport = (format) => {
    onExport(format);
    setShowExportMenu(false);
  };

  const handleShare = (method) => {
    onShare(method);
    setShowShareMenu(false);
  };

  return (
    <div className="bg-card border-b border-border px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Left Section - File Operations */}
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={onSave}
              iconName="Save"
              iconPosition="left"
              iconSize={16}
            >
              Save
            </Button>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Export
              </Button>

              {showExportMenu && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg z-50">
                  {exportOptions?.map((option) => (
                    <button
                      key={option?.id}
                      onClick={() => handleExport(option?.id)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <Icon name={option?.icon} size={16} />
                      {option?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareMenu(!showShareMenu)}
                iconName="Share"
                iconPosition="left"
                iconSize={16}
              >
                Share
              </Button>

              {showShareMenu && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg z-50">
                  {shareOptions?.map((option) => (
                    <button
                      key={option?.id}
                      onClick={() => handleShare(option?.id)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <Icon name={option?.icon} size={16} />
                      {option?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-border mx-2" />

            {/* Undo/Redo */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onUndo}
              disabled={!canUndo}
              iconName="Undo"
              iconSize={16}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={onRedo}
              disabled={!canRedo}
              iconName="Redo"
              iconSize={16}
            />
          </div>

          {/* NEW: SharePoint Upload Button */}
          <button
            onClick={onSharePointUpload}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            title="Upload to SharePoint for collaborative editing"
          >
            <Icon name="Upload" size={14} />
            <span className="hidden sm:inline">SharePoint</span>
          </button>

          {/* Center Section - Formatting Tools */}
          <div className="flex items-center gap-1">
            {/* Text Formatting */}
            <div className="flex items-center gap-1 px-2">
              {formatButtons?.map((button) => (
                <Button
                  key={button?.id}
                  variant="ghost"
                  size="sm"
                  iconName={button?.icon}
                  iconSize={16}
                  className="w-8 h-8"
                />
              ))}
            </div>

            <div className="h-6 w-px bg-border mx-2" />

            {/* Alignment */}
            <div className="flex items-center gap-1 px-2">
              {alignmentButtons?.map((button) => (
                <Button
                  key={button?.id}
                  variant="ghost"
                  size="sm"
                  iconName={button?.icon}
                  iconSize={16}
                  className="w-8 h-8"
                />
              ))}
            </div>

            {documentType === 'excel' && (
              <>
                <div className="h-6 w-px bg-border mx-2" />
                <div className="flex items-center gap-1 px-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Table"
                    iconSize={16}
                    className="w-8 h-8"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Calculator"
                    iconSize={16}
                    className="w-8 h-8"
                  />
                </div>
              </>
            )}
          </div>

          {/* Right Section - Status & Collaboration */}
          <div className="flex items-center gap-3">
            {/* Auto-save Status */}
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${
                autoSaveStatus === 'saved' ? 'bg-success' : 
                autoSaveStatus === 'saving' ? 'bg-warning' : 'bg-error'
              }`} />
              <span className="text-muted-foreground">
                {autoSaveStatus === 'saved' ? 'All changes saved' :
                 autoSaveStatus === 'saving' ? 'Saving...' : 'Error saving'}
              </span>
            </div>

            {/* Collaboration Indicator */}
            {isCollaborating && (
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 bg-primary rounded-full border-2 border-background flex items-center justify-center">
                    <span className="text-xs text-primary-foreground font-medium">JD</span>
                  </div>
                  <div className="w-6 h-6 bg-secondary rounded-full border-2 border-background flex items-center justify-center">
                    <span className="text-xs text-secondary-foreground font-medium">AS</span>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">2 editing</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Click outside handlers */}
      {(showExportMenu || showShareMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowExportMenu(false);
            setShowShareMenu(false);
          }}
        />
      )}
    </div>
  );
};

export default DocumentToolbar;